import { ScaleTime, ScaleLinear, ScaleBand } from "d3"
import { DataSource } from "./DataSource"
import { ColorSpectrum } from "./ColorSpectrum"
import type { TraceGoal } from "./AnalysisDetails"
import { DEFAULT_DSA_DISPLAY_SCALE, DSADisplayScale, getDSAFrequencyLabels, getQEEGDSARequestAnalysis, getSourceDSAFrequencyLabels, isOfflineQEEGDSAFileName, isQEEGDSAAnalysis, isQEEGDSASourceModality, isSpectrogramAnalytic, OnDemandAnalysis } from "./AnalysisDetails"
import { ColorRGB } from "./Color"
import { SummaryValueConfig } from "./Graph"
import { EnvironmentVariables } from "../../../../../Providers/EnvironmentVariablesProvider"

export type BaseTraceConfigJSON = {
	id: string
	dataKey: string
	dataSource: DataSource
	name: string
	units: string
	rawDataKey: string
	onDemandAnalysis?: OnDemandAnalysis
	summaryValueConfig?: SummaryValueConfig
	isVisible?: boolean // default to true if undefined
}

// Some traces are part of composite data types. EEG, CPPOPT, etc.
// So, we need to provide the index of the composite data that they belong to.
export type CompositePartTraceConfigJSON = BaseTraceConfigJSON & {
	isCompositePart: true
	compositeIndex: number
}

// If the trace is not a composite part, then it does not have a composite index.
export type NonCompositePartTraceConfig = BaseTraceConfigJSON & {
	isCompositePart: false
}

export type TraceDataConfig = CompositePartTraceConfigJSON | NonCompositePartTraceConfig

export type SolidColorTraceDataConfig = TraceDataConfig & WithSolidColor

export type TraceConfigJSON = LineTraceConfigJSON | HeatmapTraceConfigJSON | SpectrogramTraceConfigJSON | DeltaRegionJSON

export type TraceConfigJSONWithDataObjectId = (TraceConfigJSON & { dataObjectId: number }) | DeltaRegionWithDataObjectId

type DeltaRegionWithDataObjectId = DeltaRegion & {
	first: BaseTraceConfig & TraceDataConfig & { dataObjectId: number }
	second: BaseTraceConfig & TraceDataConfig & { dataObjectId: number }
}

export type TraceConfig = LineTraceConfig | HeatmapTraceConfig | SpectrogramTraceConfig | DeltaRegion

export type BaseTraceConfig = {
	graphId: string
	xScale: ScaleTime<any, any, any>
	isVisible?: boolean
}

export type LineTraceConfig = LineTraceConfigJSON & BaseTraceConfig & {
	yScale: ScaleLinear<any, any, any>
}

export type HeatmapTraceConfig = HeatmapTraceConfigJSON & BaseTraceConfig & {
	yScale: ScaleBand<any>
}

export type PageRectangle = {
	x: number
	y: number
	width: number
	height: number
}

export enum RenderStrategy {
	LINE = "line",
	LINE_STEP = "line_step",
	HEATMAP = "heatmap",
	IMAGE = "image",
	DELTA = "delta",
	SPECTROGRAM = "spectrogram"
}

type BaseTraceConfigSeed = Pick<BaseTraceConfigJSON, "name" | "dataKey" | "rawDataKey" | "dataSource" | "units" | "onDemandAnalysis">

export type TraceConfigSeed = BaseTraceConfigSeed & (
	| { isCompositePart: true; compositeIndex: number }
	| { isCompositePart: false }
)

export type TraceOption = {
	label: string
	source: string
	value: TraceConfigSeed
	supportedRenderStrategies: RenderStrategy[]
	highlight: boolean
}

// The key is JSON.stringify of the request analysis, so property insertion order is
// load-bearing: all producers must build the analysis via getQEEGDSARequestAnalysis.
// Do NOT canonicalize (e.g. sort keys) here.
export const getSpectrogramDataKey = (rawDataKey: string, analysis: OnDemandAnalysis | undefined): string => (
	isQEEGDSAAnalysis(analysis)
		? JSON.stringify(getQEEGDSARequestAnalysis(analysis))
		: rawDataKey
)

// Which graph types a trace may be rendered as. The single place this is
// decided, for both source modalities and analytics.
export const getSupportedRenderStrategies = (
	seed: Pick<TraceConfigSeed, "rawDataKey" | "onDemandAnalysis">,
	context: { environmentVariables: EnvironmentVariables, fileName: string | undefined },
): RenderStrategy[] => {
	if (isSpectrogramAnalytic(seed.onDemandAnalysis?.analytic)) {
		return [RenderStrategy.SPECTROGRAM]
	}

	// Offline-computed DSA is stored in archives as a qEEG_DSA composite, so it
	// shows up as a source modality. It is a frequency-by-time frame: a
	// spectrogram is its only meaningful rendering -- and only when the feature
	// flag is on and the file is an offline export (`...,MCP`), not a computed
	// analytic. Otherwise it supports nothing: fail closed rather than offer a
	// line rendering of a frequency frame.
	if (isQEEGDSASourceModality(seed.rawDataKey)) {
		const isAvailable = context.environmentVariables.FEATURE_FLAG_QEEG_DSA_SOURCE && isOfflineQEEGDSAFileName(context.fileName)
		return isAvailable ? [RenderStrategy.SPECTROGRAM] : []
	}

	return [RenderStrategy.LINE, RenderStrategy.HEATMAP]
}

export type DeltaRegionJSON = {
	id: string
	renderStrategy: RenderStrategy.DELTA
    first: BaseTraceConfigJSON & TraceDataConfig
	second: BaseTraceConfigJSON & TraceDataConfig
	color: ColorRGB
	name: string
	summaryValueConfig?: SummaryValueConfig
	isVisible?: boolean
}

export type DeltaRegion = DeltaRegionJSON & BaseTraceConfig & {
	first: BaseTraceConfig & TraceDataConfig
	second: BaseTraceConfig & TraceDataConfig
	yScale: ScaleLinear<any, any, any>
}

export type LineTraceConfigJSON = TraceDataConfig & WithSolidColor & {
	goal?: TraceGoal // the measurement's goal and how it is shown (Types/GoalRanges.ts)
	goalPercent?: { dataKey: string, binMs: number, timeZone?: string } // derived, never saved: its goal strip's data, for % in goal under its value
	renderStrategy: RenderStrategy.LINE | RenderStrategy.LINE_STEP
	color: string
}

export type HeatmapTraceConfigJSON = TraceDataConfig & WithColorSpectrum & {
	renderStrategy: RenderStrategy.HEATMAP
	lowerBound: number
	upperBound: number
	toggleTarget?: { graphId: string, traceId: string } // a goal strip row: its label toggles this measurement
}

export const getHeatmapTraceId = (config: HeatmapTraceConfigJSON) => config.id

export type SpectrogramTraceConfigJSON = TraceDataConfig & WithColorSpectrum & {
	renderStrategy: RenderStrategy.SPECTROGRAM
	displayScale: DSADisplayScale
	lowerBound: number
	upperBound: number
}

export type SpectrogramTraceConfig = SpectrogramTraceConfigJSON & BaseTraceConfig & {
	yScale: ScaleBand<string>
}

// Both the analytic and the offline archive compute one DSA column per 8 s epoch.
export const SPECTROGRAM_EPOCH_MS = 8000

// A source (offline) DSA trace has no analysis; its stored frame is on the fixed
// 40-bin archive grid, which query verifies fail-closed before serving. Its labels
// come from the immutable source contract, not the analytic's display band.
export const getSpectrogramFrequencyLabels = (config: SpectrogramTraceConfigJSON): string[] => {
	if (isQEEGDSAAnalysis(config.onDemandAnalysis)) {
		return getDSAFrequencyLabels()
	}

	if (config.onDemandAnalysis === undefined && isQEEGDSASourceModality(config.rawDataKey)) {
		return getSourceDSAFrequencyLabels()
	}

	return []
}

export interface SpectrogramTraceSettings {
	displayScale: DSADisplayScale
	colorSpectrum: ColorSpectrum
	lowerBound: number
	upperBound: number
}

export const DEFAULT_SPECTROGRAM_TRACE_SETTINGS: SpectrogramTraceSettings = {
	displayScale: DEFAULT_DSA_DISPLAY_SCALE,
	colorSpectrum: ColorSpectrum.DISCRETE_RGB,
	lowerBound: 0,
	upperBound: 2,
}

export type WithSolidColor = { color: string }

export type WithColorSpectrum = { colorSpectrum: ColorSpectrum }

export const getFullTraceDataConfigs = (trace: TraceConfigJSON | TraceConfigJSONWithDataObjectId): (BaseTraceConfigJSON & TraceDataConfig)[] => {
	// Because of delta regions (which have 2 trace configurations) we need to be able to cleanly 
	// get the information we need to request data from the backend. This happens pretty often.

	switch (trace.renderStrategy) {
		case RenderStrategy.DELTA:
			return [trace.first, trace.second]
		default:
			return [trace]
	}
}

export type UpdateTraceOptions = {
	useModalitySpecificDefaults: boolean
}
